/* @flow */
import * as React from "react";
import { compose } from "redux";
import moment from "moment";
import { connect } from "react-redux";
import querystring from "querystring";
import { withRouter } from "react-router-dom";
import clamp from "lodash/clamp";
import styled from "styled-components";
import idx from "idx";

import { store } from "../App.js";

import { gaReducer } from "./GA";
import { Link as StylelessLink } from "./common/Link";
import Gettext, { withGettext } from "./common/Gettext";
import {
  runBehaviourTrigger,
  config as behaviourConfig
} from "../configs/BehaviourTriggers";
// import { ToastContainer, toast } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import { debugMode } from "./constants";

import EarlySalePopup from "../components/TradeModals/EarlySalePopup";
import BehaviourTriggersPopup from "../components/TradeModals/BehaviourTriggersPopup";
import { mapDispatchToFetch } from "../components/common/fetch";
import withData from "../components/withData";
import { TradeThemeProvider } from "../components/ThemeProvider";
import RtlProvider from "../components/RtlProvider";
import Tournament, { ActiveTournament } from "./Tournaments/Api";
// import {
//   calcBalancePercentage,
//   disableRefill
// } from "./Tournaments/helpers/refill";

import { getViewedStock, TABS_LIMIT } from "../redux/reducer/tabs";
import { decryptConnectionState } from "../redux/reducer/socket";
import { getTimeUntilNextLockToggle } from "../redux/reducer/stocks";
import type { ServerData } from "../redux/reducer/serverData";
import type { AugmentedAccount } from "../redux/reducer/accounts";

import type { State } from "../redux/reducer";
import type { Dispatch } from "../redux/action";
import type {
  Deal,
  Stock,
  Profile,
  OperationClosed
} from "../redux/CommonTypes";
import Tour from "./Tour";
import FirstSlide from "./Sightseeing/Intro";
import LastSlide from "./Sightseeing/Outro";

const Link = React.memo(styled(StylelessLink)`
  color: #03a9f4;
`);

export type LayoutProps = {
  showInsufficientFundsMessage: boolean,
  dismissInsufficientFundsMessage: () => void,
  showVerificationRequired: boolean,
  dismissVerificationRequiredMessage: () => void,
  showIncreaseBidLimit: boolean,
  showIncreaseBidLimitMessage: () => void,
  dismissIncreaseBidLimitMessage: () => void,
  showTradeIsClosing: boolean,
  adsMessage?: string,
  untilNextReconnect: number,
  refillDemoAccount: () => Promise<*>,
  tryMakingADeal: (deal: Deal) => Promise<*>,
  isDealAmountValid: boolean,
  validateDealAmount: (key: boolean) => void,
  refreshProfile: () => Promise<*>,
  showTradeIsClosing: boolean,
  dismissTradeIsClosing: () => void,
  connectionState: string,
  untilNextReconnect: number,
  retrySocket: () => void,
  isIOS?: boolean,
  stock: Stock,
  accountType: "real" | "demo",
  possibleTradeIntervals: Array<number>,
  currentTradeInterval: number,
  tradeControlsDisabled: boolean,
  timeUntilNextLockToggle: number,
  dealsToCompensate: Array<OperationClosed>,
  onCompensation: () => void,
  compensationUpdated: Object,
  compensationCreated: Object,
  onCompensationCreated: () => void
};

type TradingPlatformProps = {
  profile: Profile,
  accounts: Array<AugmentedAccount>,
  hasActiveDeals: boolean,
  serverData: ServerData,
  socket: $PropertyType<State, "socket">,
  theme: string,
  tabs: $PropertyType<State, "tabs">,
  dispatch: Dispatch,
  layout: React.ComponentType<LayoutProps>,
  retrySocket: () => void,
  dismissErrorMessage: () => void,
  fetch: (string, ?Object) => Promise<Object>,
  stocks: { [string]: Stock },
  accountType: "real" | "demo",
  possibleTradeIntervals: Array<number>,
  currentTradeInterval: number,
  tradeControlsDisabled: boolean,
  timeUntilNextLockToggle: number
};

type TradingPlatformState = {
  showInsufficientFundsMessage: boolean,
  showVerificationRequired: boolean,
  showIncreaseBidLimit: boolean,
  showTradeIsClosing: boolean,
  isDealAmountValid: boolean,
  adsMessage: string,
  dealsToCompensate: Array<OperationClosed>
};

let earlyClosePopupShown = false;

class TradingPlatform extends React.Component<
  TradingPlatformProps,
  TradingPlatformState
> {
  state = {
    refundFetching: new Set(),
    showInsufficientFundsMessage: false,
    showVerificationRequired: false,
    showIncreaseBidLimit: false,
    showTradeIsClosing: false,
    isDealAmountValid: true,
    errorMessage: "",
    adsMessage: "",
    dealsToCompensate: [],
    compensationUpdated: null,
    compensationCreated: null,
    isTriggerPopupOpened: false,
    showDemoTournament: false,
    demoTournament: null
  };

  constructor(props) {
    super(props);

    const sameIntervalDealsTrigger = runBehaviourTrigger(
      behaviourConfig.sameIntervalDeals,
      () => {
        console.log("sameIntervalDeals trigger complete");
        // toast(this.props.gettext`Try to change the expiration time. Sometimes it's exactly what it takes to win.`);

        this.props.showModal(BehaviourTriggersPopup, {
          text: this.props
            .gettext`Try to change the expiration time. Sometimes it's exactly what it takes to win.`,
          dismissTriggerPopup: () =>
            this.setState({ isTriggerPopupOpened: false })
        });

        gaReducer("TRIGGER_INTERVALS_SHOW");
      },
      { storageKeyPrefix: this.props.profile.id }
    );

    const sameStockDealsTrigger = runBehaviourTrigger(
      behaviourConfig.sameStockDeals,
      () => {
        console.log("sameStockDeals trigger complete");
        // toast(this.props.gettext`Try to use another asset. Probably you can have better profits with it.`);

        this.props.showModal(BehaviourTriggersPopup, {
          text: this.props
            .gettext`Try to use another asset. Probably you can have better profits with it.`,
          activeAccount: this.getActiveAccount(),
          onStockClick: stock => {
            this.recommendedStockClickHandler(stock);
            gaReducer("TRIGGER_STOCKS_CLICK", { stock });
          },
          recommendations: this.getRecommendedStocks(),
          dismissTriggerPopup: () =>
            this.setState({ isTriggerPopupOpened: false })
        });

        const stock = getViewedStock(this.props.tabs);
        gaReducer("TRIGGER_STOCKS_SHOW", { stock });
      },
      { storageKeyPrefix: this.props.profile.id }
    );

    const lowProfitWinTrigger = runBehaviourTrigger(
      behaviourConfig.lowProfitWin,
      () => {
        console.log("lowProfitWin trigger complete");
        // toast(
        //   this.props.gettext`Pay attention to the profit percentage.` + " " +
        //   this.props.gettext`Try to use another asset. Probably you can have better profits with it.`);

        this.props.showModal(BehaviourTriggersPopup, {
          text:
            this.props.gettext`Pay attention to the profit percentage.` +
            " " +
            this.props
              .gettext`Try to trade different assets and earn maximum profits!`,
          activeAccount: this.getActiveAccount(),
          onStockClick: stock => {
            this.recommendedStockClickHandler(stock);
            gaReducer("TRIGGER_PERCENT_CLICK", { stock });
          },
          recommendations: this.getRecommendedStocks(),
          dismissTriggerPopup: () =>
            this.setState({ isTriggerPopupOpened: false })
        });

        const stock = getViewedStock(this.props.tabs);
        gaReducer("TRIGGER_PERCENT_SHOW", { stock });
      },
      { storageKeyPrefix: this.props.profile.id }
    );

    const bigLossTrigger = runBehaviourTrigger(
      behaviourConfig.bigLoss,
      () => {
        console.log("bigLoss trigger complete");
        // toast("bigLoss trigger complete");

        this.props.showModal(BehaviourTriggersPopup, {
          text:
            // prettier-ignore
            <Gettext>
              Maybe you should stop now and take a rest? It might be even worse
              further. Learn more about risk management here: <Link to="/news/effectivetrade" onClick={this.props.hideModal}>
                <span style={{ textTransform: "lowercase" }}>
                  <Gettext>News</Gettext>
                </span>
              </Link>
            </Gettext>,
          dismissTriggerPopup: () =>
            this.setState({ isTriggerPopupOpened: false })
        });

        gaReducer("TRIGGER_LOSS_SHOW");
      },
      { storageKeyPrefix: this.props.profile.id }
    );

    const threeDealsInARowTrigger = runBehaviourTrigger(
      behaviourConfig.threeDealsInARow,
      () => {
        console.log("threeDealsInARow trigger complete");
        // toast("threeDealsInARow trigger complete");

        this.props.showModal(BehaviourTriggersPopup, {
          text: "Three deals ✔",
          activeAccount: this.getActiveAccount(),
          onStockClick: this.recommendedStockClickHandler,
          recommendations: this.getRecommendedStocks(),
          dismissTriggerPopup: () =>
            this.setState({ isTriggerPopupOpened: false })
        });
      },
      { storageKeyPrefix: this.props.profile.id }
    );

    this.sameIntervalDealsTrigger = sameIntervalDealsTrigger[1];
    this.sameStockDealsTrigger = sameStockDealsTrigger[1];
    this.lowProfitWinTrigger = lowProfitWinTrigger[0];
    this.bigLossTrigger = bigLossTrigger[0];
    this.threeDealsInARowTrigger = threeDealsInARowTrigger[1];

    if (debugMode && window.batman === true) {
      window.sameIntervalDealsTrigger = () => {
        this.setState({ isTriggerPopupOpened: true });
        this.props.showModal(BehaviourTriggersPopup, {
          text: this.props
            .gettext`Try to change the expiration time. Sometimes it's exactly what it takes to win.`,
          dismissTriggerPopup: () =>
            this.setState({ isTriggerPopupOpened: false })
        });
      };

      window.sameStockDealsTrigger = () => {
        this.setState({ isTriggerPopupOpened: true });
        this.props.showModal(BehaviourTriggersPopup, {
          text: this.props
            .gettext`Try to use another asset. Probably you can have better profits with it.`,
          activeAccount: this.getActiveAccount(),
          onStockClick: this.recommendedStockClickHandler,
          recommendations: this.getRecommendedStocks(),
          dismissTriggerPopup: () =>
            this.setState({ isTriggerPopupOpened: false })
        });
      };

      window.lowProfitWinTrigger = () => {
        this.setState({ isTriggerPopupOpened: true });
        this.props.showModal(BehaviourTriggersPopup, {
          text:
            this.props.gettext`Pay attention to the profit percentage.` +
            " " +
            this.props
              .gettext`Try to trade different assets and earn maximum profits!`,
          activeAccount: this.getActiveAccount(),
          onStockClick: this.recommendedStockClickHandler,
          recommendations: this.getRecommendedStocks(),
          dismissTriggerPopup: () =>
            this.setState({ isTriggerPopupOpened: false })
        });
      };

      window.bigLossTrigger = () => {
        this.setState({ isTriggerPopupOpened: true });
        this.props.showModal(BehaviourTriggersPopup, {
          text:
            // prettier-ignore
            <Gettext>
              Maybe you should stop now and take a rest? It might be even worse
              further. Learn more about risk management here: <Link to="/news/effectivetrade" onClick={this.props.hideModal}>
                <span style={{ textTransform: "lowercase" }}>
                  <Gettext>News</Gettext>
                </span>
              </Link>
            </Gettext>,
          dismissTriggerPopup: () =>
            this.setState({ isTriggerPopupOpened: false })
        });
      };
    }
  }

  getActiveAccount = () => {
    const activeAccount = this.props.accounts.find(
      account => account.id === this.props.profile.active_account_id
    );

    return activeAccount;
  };

  getRecommendedStocks = () => {
    const activeAccount = this.getActiveAccount();

    // const foo = filterPopular(
    //   this.props.stocks,
    //   activeAccount && activeAccount.type === "real"
    //     ? item => item.realProfit
    //     : item => item.demoProfit,
    //   3);

    // const recommendations = Object.keys(foo)
    //   .map(item => foo[item])

    const recommendations =
      Object.keys(this.props.stocks)
        .map(symbol => this.props.stocks[symbol])
        .filter(
          stock => stock.enabled && !this.props.tabs.tabs.includes(stock.stock)
        )
        .sort((a, b) =>
          activeAccount && activeAccount.type === "real"
            ? b.realProfit - a.realProfit
            : b.demoProfit - a.demoProfit
        )
        .slice(0, 3) || [];

    return recommendations;
  };

  validateDealAmount = (isDealAmountValid: boolean) => {
    this.setState({
      isDealAmountValid
    });
  };

  showIncreaseBidLimitMessage = () => {
    this.setState({ showIncreaseBidLimit: true });
  };

  dismissIncreaseBidLimitMessage = () => {
    const { profile } = this.props;

    let bid_limit = 5000;

    if (profile) {
      if (profile.bid_limit) {
        bid_limit =
          typeof profile.bid_limit === "object"
            ? profile.bid_limit[profile.currency]
            : profile.bid_limit;
      }
    }

    this.props.dispatch({
      type: "UPDATE_SETTINGS",
      payload: {
        currentBid: bid_limit
      }
    });
    this.setState({ showIncreaseBidLimit: false });
  };

  dismissInsufficientFundsMessage = () => {
    this.setState({ showInsufficientFundsMessage: false });
  };

  dismissErrorMessage = () => {
    this.setState({ errorMessage: "" });
  };

  dismissVerificationRequiredMessage = () => {
    this.setState({ showVerificationRequired: false });
  };

  dismissTradeIsClosing = () => {
    this.setState({ showTradeIsClosing: false });
  };

  dismissDemoTournament = () => {
    this.setState({ showDemoTournament: false });
  };

  handleError = error => {
    console.log(error);
  };

  refillDemoAccount = () => {
    this.setState({ isRefillFetching: true });

    return this.props
      .fetch("/ajax/refillDemoDeposit")
      .then(this.refreshProfile)
      .then(() => {
        this.props.dispatch({
          type: "REQUEST_ACCOUNTS"
        });
      })
      .catch(this.handleError)
      .then(() => this.setState({ isRefillFetching: false }));
  };

  refundClickHandler = (dealId: number) => {
    const refundFetching = new Set(this.state.refundFetching);
    refundFetching.add(dealId);
    this.setState({ refundFetching });

    return this.props
      .fetch(`/api/trading/op-close-early?op_id=${dealId}`)
      .catch(() => {
        const refundFetching = new Set(this.state.refundFetching);
        refundFetching.delete(dealId);
        this.setState({ refundFetching });
      })
      .then(() => {
        // Ничего не делаем при успешном закрытии, чтобы компоненты закрытия сделки
        // на графике и в истории не вернулись в исходное состояние.
        // При получении сообщения в сокете о закрытии сделки они пропадут вместе со сделкой
      });
  };

  refreshProfile = () => {
    this.props.dispatch({ type: "REQUEST_PROFILE" });
    let p = new Promise(r => r());

    return p;
  };

  tryMakingADeal = (deal: Deal) => {
    if (deal.accountType === "real") {
      this.threeDealsInARowTrigger();
    }

    return this.props.mode === "tournament"
      ? this.tryMakingADealTournament(deal)
      : this.tryMakingADealBinary(deal);
  };

  async tryMakingADealTournament(deal: Deal) {
    // Сначала — валидации
    let active_tournament = this.props.activeTournament.value;

    if (active_tournament == null) {
      this.props.tournament.simulateError("not_running_tournament");
      console.error("No tournament chosen");
    }

    if (active_tournament.participation_status !== 1) {
      this.props.tournament.simulateError("not_running_tournament");
      console.error("Somehow the chosen tournament is not being participated");
    }

    let { account } = active_tournament;

    if (deal.amount > parseFloat(account.balance)) {
      console.error("You don't have enough money to make this deal");
      this.validateDealAmount(false);
      // const refillSum = calcBalancePercentage(active_tournament);
      // const disabledRefill = disableRefill({
      //   api: this.props.tournament
      // });
      //
      // switch (true) {
      //   case parseFloat(account.balance) > refillSum:
      //     this.props.tournament.simulateError("no_refill_no_bid");
      //     console.error("refill % is not sufficient for refill");
      //     break;
      //   case disabledRefill:
      //     this.props.tournament.simulateError("balance_not_low_refill");
      //     console.error("refill is not possible");
      //     break;
      //   default:
      //     this.setState({
      //       showTournamentRefill: true
      //     });
      //     break;
      // }

      return;
      // throw new TypeError("Not enough money");
    }

    if (
      this.props.timeUntilNextLockToggle !== Infinity &&
      this.props.currentTradeInterval * 1000 >=
        this.props.timeUntilNextLockToggle
    ) {
      this.setState({
        showTradeIsClosing: true
      });
      // throw new TypeError("Trade is closing soon");
      return;
    }

    let query = querystring.stringify({
      stock: deal.stock,
      account_id: account.id,
      interval: `${deal.bTime}`,
      amount: deal.amount,
      type: deal.bidType ? 1 : 2,
      profit_percent:
        this.props.stocks[deal.stock].realProfit -
        this.props.stocks[deal.stock].profit_vip,
      serverTime: deal.date,
      value: deal.value
    });

    let promise = this.props.fetch("/api/tournaments/openOperation?" + query);

    this.props.activeTournament.update(
      tournament => ({
        ...tournament,
        account: {
          ...tournament.account,
          balance: (
            parseFloat(tournament.account.balance) - deal.amount
          ).toFixed(2)
        }
      }),
      promise
    );

    const res = await promise;
    //Бэк будет сам разруливать эту тему передавая в сделку нужные параметры
    // const features = this.props.tournament.readEntity("/api/features/list");
    // const closeEarly =
    //   features.value &&
    //   features.value.find(feature => feature.name === "operation_early_close");

    if (res.payload && res.payload.earlyClose) {
      this.earlySaleCounters();
    }

    // Инвалидировать стату (пока нет апи)
    // this.props.tournament.fetchEntity("/api/tournaments/stats");

    // инвалидировать сделки
    this.props.tournament.fetchEntity(
      "/api/tournaments/operations?account_id=" + account.id,
      { force: true }
    );
  }

  async tryMakingADealBinary(deal: Deal) {
    const { profile, accounts, stocks, dispatch } = this.props;

    let bid_limit = 5000;

    if (profile) {
      if (profile.bid_limit) {
        bid_limit =
          typeof profile.bid_limit === "object"
            ? profile.bid_limit[profile.currency]
            : profile.bid_limit;
      }
    }

    if (profile === null) {
      // TODO: Indicate error
      throw new TypeError("No profile");
    }

    if (accounts.length === 0) {
      // TODO: Indicate error
      throw new TypeError("No accounts");
    }

    const activeAccount = accounts.find(
      account => account.id === profile.active_account_id
    );

    if (typeof activeAccount !== "object") {
      // TODO: Indicate error
      throw new TypeError("No active account, how's that possible?");
    }

    if (deal.amount > parseFloat(activeAccount.estimatedBalance)) {
      this.setState({
        showInsufficientFundsMessage: true
      });
      return;
      // throw new TypeError("Not enough money");
    }

    if (
      activeAccount &&
      activeAccount.type !== "demo" &&
      deal.amount > bid_limit
    ) {
      this.setState({
        showIncreaseBidLimit: true
      });
      // throw new TypeError("Maximum bid exceeded");
      return;
    }

    if (
      this.props.timeUntilNextLockToggle !== Infinity &&
      this.props.currentTradeInterval * 1000 >=
        this.props.timeUntilNextLockToggle
    ) {
      this.setState({
        showTradeIsClosing: true
      });
      // throw new TypeError("Trade is closing soon");
      return;
    }

    dispatch({
      type: "REQUEST_DEAL_OPENING",
      payload: deal
    });

    const query = querystring.stringify({
      type: deal.bidType ? 1 : 2,
      stock: deal.stock,
      interval: `${deal.bTime}`,
      amount: deal.amount,
      value: deal.value,
      accountId: profile.active_account_id,
      profitPercent:
        activeAccount.type === "real"
          ? stocks[deal.stock].realProfit
          : stocks[deal.stock].demoProfit,
      serverTime: deal.date
    });

    await new Promise((resolve, reject) => {
      this.setState(
        {
          isFetchingOpenOperation: true
        },
        resolve
      );
    });

    const response = await this.props
      .fetch(`/ajax/openOperation?${query}`)
      .then(res => {
        if (res.type === "verificationRequired") {
          this.setState({
            showVerificationRequired: true
          });
        }

        return res;
      })
      .then(res => {
        this.setState({ isFetchingOpenOperation: false });

        return res;
      });

    //Бэк будет сам разруливать эту тему передавая в сделку нужные параметры
    //const features = this.props.tournament.readEntity("/api/features/list");
    //  const closeEarly =
    //    features.value &&
    //    features.value.find(feature => feature.name === "operation_early_close");
    if (
      response.type !== "verificationRequired" &&
      response.earlyClose
    ) {
      this.earlySaleCounters();
    }

    dispatch({
      type: "RECEIVE_DEAL_OPENING_SUCCESS",
      payload: {
        ...deal,
        id: Math.random()
          .toString(36)
          .slice(2, 8),
        timestamp: response.openTimestamp / 1000,
        open_timestamp: response.openTimestamp / 1000
      }
    });
  }

  earlySaleCounters = () => {
    try {
      const shownKey = `${this.props.profile.id}:earlyClosePopup`;
      const storageShown = localStorage.getItem(shownKey);

      if (JSON.parse(storageShown) !== true) {
        const key = `${this.props.profile.id}:earlyCloseDealsCount`;
        const storage = localStorage.getItem(key);

        let count = 1;
        if (JSON.parse(storage) != null) {
          count = parseInt(storage, 10) + 1;
        }

        if (count < 5) {
          localStorage.setItem(key, count);
        }

        if (count >= 5 && !earlyClosePopupShown) {
          this.props.showModal(EarlySalePopup, {
            userId: this.props.profile.id,
            lang: this.props.profile.locale
          });
          earlyClosePopupShown = true;
        }
      }
    } catch (e) {}
  };

  requestStockHistory = () => {
    const stock = getViewedStock(this.props.tabs);
    const limit = 7200; //2 hours

    return this.props
      .fetch(`/ajax/stockHistory?stock=${stock}&limit=${limit}&tz=UTC`)
      .then(history => {
        this.props.dispatch({
          type: "RECEIVE_STOCK_HISTORY",
          payload: { stock, history }
        });
      })
      .catch(this.handleError);
  };

  recommendedStockClickHandler = (stock: string) => {
    this.props.dispatch({
      type: "SET_ACTIVE_STOCK",
      payload: {
        newStock: stock,
        activeStock: this.props.activeStock
      }
    });
  };

  handleReduxAction = action => {
    if (action.type === "RECEIVE_SOCKET_MESSAGE") {
      switch (action.payload.op) {
        //case "tournamentOperationOpened":
        case "tournamentOperationClosed": {
          this.setState({
            isDealAmountValid: true
          });
          this.props.tournament.fetchEntity("/api/tournaments/stats");
          this.props.tournament.fetchEntity(
            "/api/tournaments/operations?account_id=" +
              action.payload.accountId,
            { force: true }
          );

          break;
        }
        case "tournament-status-change":
          this.props.tournament.fetchEntity("/api/tournaments/stats");
          break;
        case "operationClosed":
          if (action.payload.can_compensate === true) {
            this.setState({
              dealsToCompensate: this.state.dealsToCompensate.concat(
                action.payload
              )
            });
          } else if (!this.state.isTriggerPopupOpened) {
            const configLostPercent = this.bigLossTrigger.params.percent;
            const lossPercent =
              (action.payload.profit * 100) /
              (action.payload.profit - action.payload.balance);

            let a = this.bigLossTrigger.trigger(null, {
              profitable:
                action.payload.profit >= 0 || action.payload.real === 0,
              percent: configLostPercent,
              lowPercent: lossPercent >= configLostPercent
            });

            if (a === true) {
              this.setState({ isTriggerPopupOpened: true });
              break;
            }

            let isVIP = this.props.serverData.isVIP;

            const profitPercent = this.lowProfitWinTrigger.params.percent;
            const vipProfitPercent = this.lowProfitWinTrigger.params.vipPercent;
            const payloadPercent = action.payload.profitPercent;
            let b = this.lowProfitWinTrigger.trigger(null, {
              profitable:
                action.payload.profit > 0 && action.payload.real === 1,
              percent: profitPercent,
              vipPercent: vipProfitPercent,
              lowPercent:
                (isVIP && payloadPercent < vipProfitPercent) ||
                (!isVIP && payloadPercent < profitPercent)
            });

            if (b === true) {
              this.setState({ isTriggerPopupOpened: true });
              break;
            }

            let c = this.sameStockDealsTrigger(action.payload.stock, {
              profitable:
                action.payload.profit >= 0 || action.payload.real === 0
            });

            if (c === true) {
              this.setState({ isTriggerPopupOpened: true });
              break;
            }

            let d = this.sameIntervalDealsTrigger(action.payload.bidTime, {
              profitable:
                action.payload.profit >= 0 || action.payload.real === 0
            });

            if (d === true) {
              this.setState({ isTriggerPopupOpened: true });
              break;
            }
          }
          break;
        case "compensation-updated":
          this.setState({
            compensationUpdated: action.payload
          });
          break;
        case "compensation-created":
          this.setState({
            compensationCreated: action.payload
          });
          break;
        case "demo-tournament":
          this.setState({
            demoTournament: action.payload,
            showDemoTournament: true
          });
          break;
        default:
          break;
      }
    }
  };

  onCompensation = deal => {
    this.setState(s => {
      return {
        dealsToCompensate: s.dealsToCompensate.filter(d => d !== deal)
      };
    });
  };

  onCompensationCreated = async () => {
    if (
      this.state.compensationCreated != null &&
      this.state.compensationCreated.payload.real === 0
    ) {
      this.setState({
        compensationCreated: null
      });
      return;
    }

    const realAccount = this.props.accounts.find(acc => acc.type === "real");
    if (!realAccount || realAccount.isActive === true) {
      this.setState({
        compensationCreated: null
      });
      return;
    }

    const data = await this.props.fetch(
      `/ajax/setAccount?id=${realAccount.id}`
    );
    if (data.status === "OK") {
      this.props.dispatch({ type: "SWITCH_ACCOUNTS" });
    }
    const payload = await this.props.fetch("/api/profile");
    this.props.dispatch({ type: "RECEIVE_PROFILE", payload });
    this.setState({
      compensationCreated: null
    });
  };

  showDemoCompensationsPopup = async () => {
    const features = await this.props.tournament.readEntity(
      "/api/features/list"
    );
    const isDemoCompensationsAvailable =
      features.value &&
      features.value.find(feature => feature.name === "demo_compensations");

    if (!isDemoCompensationsAvailable) return;

    const compensationsInfo = await this.props.tournament.fetchEntity(
      "/api/compensations/info"
    );

    const compensations = idx(compensationsInfo, _ => _.value);

    const popupShown = () => {
      try {
        return localStorage.getItem("demoCompensationsPopupShown");
      } catch (e) {
        console.error(e);
        return true;
      }
    };

    // если у пользователя есть демокомпенсации,
    // нету реальных компенсаций,
    // не тратил демо компесанции
    // и это его первая сессия,
    // то показываем попап о начислении демокомпенсаций
    if (
      compensations.demo.count > 0 &&
      compensations.real.count < 1 &&
      compensations.demo.stats.compensated == null &&
      this.props.profile.session_count < 2 &&
      !popupShown()
    ) {
      try {
        localStorage.setItem("demoCompensationsPopupShown", true);
      } catch (e) {
        console.error(e);
      }
      this.setState({
        compensationCreated: {
          payload: {
            real: 0
          }
        }
      });
    }
  };

  componentDidMount() {
    this.showDemoCompensationsPopup();
    this.unsubscribe = store.subscribeToActions(this.handleReduxAction);

    this.props.tournament.fetchEntity("/api/tournaments/stats").then(stats => {
      let { active_tournament } = stats.value;

      if (active_tournament && active_tournament.account) {
        let { account } = active_tournament;
        this.props.tournament.fetchEntity(
          "/api/tournaments/operations?account_id=" + account.id
        );
      } else {
        this.props.dispatch({ type: "ENGAGE_BINARY_MODE" });
      }
    });

    // Случай, когда компонент примонтировался на живой
    // сокет. Такое бывает при переходе с других страниц.
    if (this.props.socket.connectionState === WebSocket.OPEN) {
      this.requestStockHistory();
    }

    const { stock, stocks, tabs } = this.props;

    if (stock && stocks[stock] && stocks[stock].enabled) {
      const index = tabs.tabs.indexOf(stock);
      if (index > -1) {
        this.props.dispatch({
          type: "SET_ACTIVE_TAB",
          payload: index
        });
      } else if (tabs.tabs.length < TABS_LIMIT) {
        this.props.dispatch({
          type: "PUSH_TAB",
          payload: stock
        });
      } else {
        this.props.dispatch({
          type: "SET_ACTIVE_STOCK",
          payload: {
            newStock: stock,
            activeStock: this.props.activeStock
          }
        });
      }
    }
  }

  componentWillUnmount() {
    this.unsubscribe();
  }

  componentDidUpdate(prevProps, prevState) {
    const viewedStock = getViewedStock(this.props.tabs);

    // История точек
    if (
      viewedStock !== getViewedStock(prevProps.tabs) ||
      (this.props.socket.connectionState !== prevProps.socket.connectionState &&
        this.props.socket.connectionState === WebSocket.OPEN)
    ) {
      this.requestStockHistory();
    }

    if (this.props.mode === "tournament") {
      let stats = this.props.tournament.readEntity("/api/tournaments/stats");

      if (
        stats.value != null &&
        (stats.value.active_tournament == null ||
          stats.value.active_tournament.account == null)
      ) {
        this.props.dispatch({ type: "ENGAGE_BINARY_MODE" });
      }
    }
  }

  original = React.createRef(null);

  originalLast = React.createRef(null);

  render() {
    const Layout = this.props.layout;

    const activeAccount = this.props.accounts.find(
      account => account.id === this.props.profile.active_account_id
    );

    if (!activeAccount) {
      console.error("activeAccount not found!");
    }

    let deals = this.props.deals;
    let tournamentWillBeClosingSoon = false;

    if (this.props.mode === "tournament") {
      let stats = this.props.tournament.readEntity("/api/tournaments/stats");

      if (
        stats != null &&
        stats.value != null &&
        stats.value.active_tournament != null &&
        stats.value.active_tournament.account != null
      ) {
        let account = stats.value.active_tournament.account;

        let operations = this.props.tournament.readEntity(
          "/api/tournaments/operations?account_id=" + account.id
        );

        if (operations != null && operations.value != null) {
          deals = [...operations.value.open, ...operations.value.closed];
        } else {
          deals = [];
        }

        // Copypasted from Settings.js
        const endTime = moment(
          stats.value.active_tournament.finish_date * 1000
        );
        const maxTime = moment
          .duration(endTime.diff(moment(this.props.time)))
          .asSeconds();
        tournamentWillBeClosingSoon =
          this.props.possibleTradeIntervals[0] > maxTime;
      } else {
        deals = [];
      }
    }

    return (
      <TradeThemeProvider>
        <RtlProvider>
          <React.Fragment>
            <Tour step="intro" originalRef={this.original}>
              {({ next, isOriginal, onLater }) => (
                <FirstSlide
                  ref={isOriginal ? this.original : null}
                  next={next}
                  onLater={onLater}
                />
              )}
            </Tour>
            <Tour step="outro" originalRef={this.originalLast}>
              {({ next, isOriginal, tour }) => (
                <LastSlide
                  ref={isOriginal ? this.originalLast : null}
                  next={next}
                  tour={tour}
                />
              )}
            </Tour>
            <Layout
              animated={this.props.animated}
              time={this.props.time}
              userId={this.props.userId}
              deals={deals}
              stocks={this.props.stocks}
              onRecommendedStockClick={this.recommendedStockClickHandler}
              activeAccount={activeAccount}
              dispatch={this.props.dispatch}
              refillDemoAccount={this.refillDemoAccount}
              refundClickHandler={this.refundClickHandler}
              refundFetching={this.state.refundFetching}
              isRefillFetching={this.state.isRefillFetching}
              isFetchingOpenOperation={this.state.isFetchingOpenOperation}
              accountsReady={this.props.accountsReady}
              dealsReady={this.props.dealsReady}
              tryMakingADeal={this.tryMakingADeal}
              refreshProfile={this.refreshProfile}
              dismissInsufficientFundsMessage={
                this.dismissInsufficientFundsMessage
              }
              dismissVerificationRequiredMessage={
                this.dismissVerificationRequiredMessage
              }
              adsMessage={this.state.adsMessage}
              showInsufficientFundsMessage={
                this.state.showInsufficientFundsMessage
              }
              showVerificationRequired={this.state.showVerificationRequired}
              showIncreaseBidLimitMessage={this.showIncreaseBidLimitMessage}
              dismissIncreaseBidLimitMessage={
                this.dismissIncreaseBidLimitMessage
              }
              showIncreaseBidLimit={this.state.showIncreaseBidLimit}
              showTradeIsClosing={this.state.showTradeIsClosing}
              dismissTradeIsClosing={this.dismissTradeIsClosing}
              connectionState={decryptConnectionState(
                this.props.socket.connectionState
              )}
              untilNextReconnect={this.props.socket.untilNextReconnect}
              retrySocket={this.props.retrySocket}
              isIOS={this.props.serverData.isIOS}
              appVersion={this.props.serverData.app_version}
              stock={this.props.stocks[getViewedStock(this.props.tabs)]}
              accountType={this.props.accountType}
              possibleTradeIntervals={this.props.possibleTradeIntervals}
              currentTradeInterval={this.props.currentTradeInterval}
              tradeControlsDisabled={
                tournamentWillBeClosingSoon || this.props.tradeControlsDisabled
              }
              timeUntilNextLockToggle={this.props.timeUntilNextLockToggle}
              validateDealAmount={this.validateDealAmount}
              isDealAmountValid={this.state.isDealAmountValid}
              dealsToCompensate={this.state.dealsToCompensate}
              onCompensation={this.onCompensation}
              compensationUpdated={this.state.compensationUpdated}
              compensationCreated={this.state.compensationCreated}
              onCompensationCreated={this.onCompensationCreated}
              demoTournament={this.state.demoTournament}
              showDemoTournament={this.state.showDemoTournament}
              dismissDemoTournament={this.dismissDemoTournament}
            />
            {/*<ToastContainer
              autoClose={false}
              closeOnClick={false}
              draggable={false}
            />*/}
          </React.Fragment>
        </RtlProvider>
      </TradeThemeProvider>
    );
  }
}

function TradingPlatformWithTournamentSupport(props) {
  return (
    <Tournament.Consumer>
      {tournament => (
        <ActiveTournament>
          {activeTournament => (
            <TradingPlatform
              {...props}
              tournament={tournament}
              activeTournament={activeTournament}
            />
          )}
        </ActiveTournament>
      )}
    </Tournament.Consumer>
  );
}

function mapStateToProps(state: State, ownProps) {
  // Апи поля приходят свыше, от withData компонента
  let { serverData, stocks, profile, accounts } = ownProps;

  const hasActiveDeals = state.deals.some(deal => deal.time != null);
  const stock = getViewedStock(state.tabs);

  const stockData = stocks[stock];
  const activeAccount = accounts.find(account => {
    return account.id === profile.active_account_id;
  });

  const isReal = activeAccount ? activeAccount.type === "real" : false;
  let intervals = isReal
    ? state.remoteTradeSettings.intervals
    : state.remoteTradeSettings.intervalsDemo;

  const currentInterval =
    state.settings.currentInterval ||
    state.remoteTradeSettings.defaultInterval ||
    60;

  const timeUntilNextLockToggle = getTimeUntilNextLockToggle(
    state.time,
    stockData
  );

  if (timeUntilNextLockToggle !== Infinity) {
    intervals = intervals.filter(interval => {
      return (
        interval === state.settings.currentInterval ||
        interval * 1000 <= timeUntilNextLockToggle
      );
    });
  }

  return {
    animated: state.settings.animatedChart === undefined ? true : state.settings.animatedChart,
    activeStock: getViewedStock(state.tabs),
    time: state.time,
    tournaments: state.tournaments,
    profile: profile,
    accounts: accounts,
    accountsReady: state.dataRequirementsStatus.accounts === "AVAILABLE",
    hasActiveDeals: hasActiveDeals,
    theme: state.settings.theme,
    serverData: serverData,
    tabs: state.tabs,
    socket: state.socket,
    referralBonus: state.referralBonus,
    stocks: stocks,
    accountType: activeAccount && activeAccount.type,
    possibleTradeIntervals: intervals,
    currentTradeInterval: clamp(
      currentInterval,
      intervals[0],
      intervals[intervals.length - 1]
    ),
    tradeControlsDisabled:
      stock.enabled === false ||
      intervals.length === 0 ||
      (intervals.length === 1 &&
        intervals[0] * 1000 >= timeUntilNextLockToggle),
    timeUntilNextLockToggle: timeUntilNextLockToggle,
    mode: state.settings.mode,
    deals: state.deals,
    dealsReady: state.dataRequirementsStatus.operations === "AVAILABLE"
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    retrySocket() {
      dispatch({ type: "SOCKET_CONNECT" });
    },
    ...mapDispatchToFetch(dispatch)
  };
}

export default compose(
  withRouter,
  withData(["serverData", "stocks", "profile", "accounts"]),
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withGettext
)(TradingPlatformWithTournamentSupport);



// WEBPACK FOOTER //
// ./src/components/TradingPlatform.js